import { PuzzleDimensions, TileId } from "./types";

// Use -1 to represent the empty tile
export const EMPTY_TILE: TileId = -1;

export interface ClassicPuzzleState {
  grid: (TileId | typeof EMPTY_TILE)[][];
  dimensions: PuzzleDimensions;
}

function createSolvedGrid(dimensions: PuzzleDimensions): (TileId | typeof EMPTY_TILE)[][] {
  const { rows, cols } = dimensions;
  const grid: (TileId | typeof EMPTY_TILE)[][] = [];
  let id = 0;
  const totalTiles = rows * cols;
  for (let r = 0; r < rows; r++) {
    const row: (TileId | typeof EMPTY_TILE)[] = [];
    for (let c = 0; c < cols; c++) {
      // Last tile (bottom-right) is empty
      if (id === totalTiles - 1) {
        row.push(EMPTY_TILE);
      } else {
        row.push(id++);
      }
    }
    grid.push(row);
  }
  return grid;
}

/**
 * Shuffle by making random valid moves from the solved state.
 * This ensures the puzzle is always solvable.
 */
export function shuffleGrid(
  dimensions: PuzzleDimensions,
  rng: () => number,
  numMoves = 100
): (TileId | typeof EMPTY_TILE)[][] {
  let grid = createSolvedGrid(dimensions);
  const { rows, cols } = dimensions;

  // Make random valid moves to shuffle
  for (let i = 0; i < numMoves; i++) {
    // Find empty tile position
    let emptyRow = -1;
    let emptyCol = -1;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (grid[r][c] === EMPTY_TILE) {
          emptyRow = r;
          emptyCol = c;
          break;
        }
      }
      if (emptyRow !== -1) break;
    }

    // Find all adjacent tiles
    const adjacent: { row: number; col: number }[] = [];
    if (emptyRow > 0) adjacent.push({ row: emptyRow - 1, col: emptyCol });
    if (emptyRow < rows - 1) adjacent.push({ row: emptyRow + 1, col: emptyCol });
    if (emptyCol > 0) adjacent.push({ row: emptyRow, col: emptyCol - 1 });
    if (emptyCol < cols - 1) adjacent.push({ row: emptyRow, col: emptyCol + 1 });

    // Pick a random adjacent tile and swap with empty
    if (adjacent.length > 0) {
      const randomAdjacent = adjacent[Math.floor(rng() * adjacent.length)];
      const tmp = grid[randomAdjacent.row][randomAdjacent.col];
      grid[randomAdjacent.row][randomAdjacent.col] = grid[emptyRow][emptyCol];
      grid[emptyRow][emptyCol] = tmp;
    }
  }

  return grid;
}

export function createInitialClassicPuzzle(
  dimensions: PuzzleDimensions,
  seed = Date.now()
): ClassicPuzzleState {
  const rng = mulberry32(seed);
  const grid = shuffleGrid(dimensions, rng);
  return {
    grid,
    dimensions
  };
}

export function isClassicSolved(state: ClassicPuzzleState): boolean {
  const { rows, cols } = state.dimensions;
  let expected = 0;
  const totalTiles = rows * cols;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const tile = state.grid[r][c];
      // Last position should be empty
      if (expected === totalTiles - 1) {
        if (tile !== EMPTY_TILE) return false;
      } else {
        if (tile !== expected) return false;
      }
      expected++;
    }
  }
  return true;
}

/**
 * Find the position of the empty tile
 */
export function findEmptyTile(state: ClassicPuzzleState): { row: number; col: number } | null {
  const { rows, cols } = state.dimensions;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      if (state.grid[r][c] === EMPTY_TILE) {
        return { row: r, col: c };
      }
    }
  }
  return null;
}

/**
 * Check if a tile position is adjacent to the empty tile
 */
export function isAdjacentToEmpty(
  state: ClassicPuzzleState,
  row: number,
  col: number
): boolean {
  const empty = findEmptyTile(state);
  if (!empty) return false;
  const rowDiff = Math.abs(row - empty.row);
  const colDiff = Math.abs(col - empty.col);
  return (rowDiff === 1 && colDiff === 0) || (rowDiff === 0 && colDiff === 1);
}

/**
 * Swap a tile with the empty tile (only if adjacent)
 */
export function swapWithEmpty(
  state: ClassicPuzzleState,
  tilePos: { row: number; col: number }
): ClassicPuzzleState | null {
  if (!isAdjacentToEmpty(state, tilePos.row, tilePos.col)) {
    return null; // Not adjacent, can't swap
  }

  const empty = findEmptyTile(state);
  if (!empty) return null;

  const copy: (TileId | typeof EMPTY_TILE)[][] = state.grid.map((row) => row.slice());
  const tmp = copy[tilePos.row][tilePos.col];
  copy[tilePos.row][tilePos.col] = copy[empty.row][empty.col];
  copy[empty.row][empty.col] = tmp;

  return {
    ...state,
    grid: copy
  };
}

// Deterministic PRNG for reproducible shuffles.
function mulberry32(a: number): () => number {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

